import * as yup from "yup";

import {
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import { Close } from "@mui/icons-material";
import Colors from "../../../assets/styles/Colors";
import { DatePicker } from "@mui/x-date-pickers";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { MdNotificationImportant, MdRefresh } from "react-icons/md";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { editUser, registerManualPayment, setCustomMonthlyPrice } from "../../store/actions/users";
import { db, fsTimestampCreador } from "../../../firebase/firebase-config";
import { getExpiredStatus, refreshToken } from "../../store/actions/auth";
import { useSelector } from "react-redux";
import { memberOptions } from "../../utils/formData";
import { styles } from "../../../assets/styles/styles";
import { useFormik } from "formik";
import { useTheme } from "@mui/material";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

function generateIbCode(name, lastname) {
  const init = ((name?.[0] ?? "") + (lastname?.[0] ?? "")).toUpperCase();
  const rand = Math.random().toString(36).toUpperCase().slice(2, 6);
  return `IB-${init}-${rand}`;
}

const defaultIb = (item) => ({
  isIBPartner: item.isIBPartner ?? false,
  ibActive: item.ibActive !== false,
  ibCode: item.ibCode ?? "",
  commissionRate: Math.round((item.commissionRate ?? 0.2) * 100),
  walletAddress: item.walletAddress ?? "",
  walletCurrency: item.walletCurrency ?? "usdttrc20",
});

const UsersEditModal = ({ onClose, open, item, refresh }) => {
  const theme = useTheme();
  const [parsedDate, setParsedDate] = useState(dayjs("2005-01-01"));
  const [expired, setexpired] = useState(false);
  const [ibSection, setIbSection] = useState(() => defaultIb(item));

  const { idToken, isAlmighty } = useSelector((s) => s.auth);
  // Subscription billing-cycle state (kept separate from formik).
  const [renewalDate, setRenewalDate] = useState(dayjs().add(30, "day"));
  const [renewalPrice, setRenewalPrice] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);
  // Owner-set custom monthly price (users/{uid}.customMonthlyPrice), if any.
  const [customPrice, setCustomPrice] = useState(item.customMonthlyPrice ?? null);
  const [customPriceInput, setCustomPriceInput] = useState(
    item.customMonthlyPrice != null ? String(item.customMonthlyPrice) : ""
  );
  const [savingCustomPrice, setSavingCustomPrice] = useState(false);
  const monthlyPrice = customPrice ?? renewalPrice;

  useEffect(() => {
    setIbSection(defaultIb(item));
    setCustomPrice(item.customMonthlyPrice ?? null);
    setCustomPriceInput(item.customMonthlyPrice != null ? String(item.customMonthlyPrice) : "");
    // Seed the cycle date from the user's current expiry, else 30 days out.
    const exp = item.planExpiresAt?.toDate
      ? dayjs(item.planExpiresAt.toDate())
      : item.planExpiresAt
      ? dayjs(item.planExpiresAt)
      : dayjs().add(30, "day");
    setRenewalDate(exp);
  }, [item]);

  // Renewal quota comes from products/{plan}.monthlyPrice (the $50 flat fee).
  useEffect(() => {
    const plan = item.currentPlanKey || item.plan || null;
    if (!plan) { setRenewalPrice(null); return; }
    let cancelled = false;
    db.collection("products").doc(plan).get()
      .then((snap) => { if (!cancelled) setRenewalPrice(snap.exists ? (snap.data().monthlyPrice ?? null) : null); })
      .catch(() => { if (!cancelled) setRenewalPrice(null); });
    return () => { cancelled = true; };
  }, [item]);

  // Always mint a fresh token for the authed call — cheap and avoids expiry edge cases.
  const getToken = async () => {
    try { return await refreshToken(); } catch (_) { return idToken; }
  };

  const handleRegisterPayment = async () => {
    const confirm = await MySwal.fire({
      title: "Registrar pago",
      html: `Se registrará un pago manual de <b>$${monthlyPrice ?? "—"}</b> y la fecha de vencimiento avanzará 30 días.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Registrar",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;
    setSavingPayment(true);
    try {
      const token = await getToken();
      const res = await registerManualPayment({ uid: item.id }, token);
      setRenewalDate(dayjs(res.planExpiresAt));
      MySwal.fire("Pago registrado", `Nueva fecha de vencimiento: ${dayjs(res.planExpiresAt).format("DD/MM/YYYY")}`, "success");
      refresh();
    } catch (e) {
      MySwal.fire("Error", e.message || "No se pudo registrar el pago.", "error");
    } finally {
      setSavingPayment(false);
    }
  };

  const handleSaveCustomPrice = async (clear = false) => {
    const price = clear ? null : Number(customPriceInput);
    if (!clear && (!Number.isFinite(price) || price <= 0 || price > 10000)) {
      MySwal.fire("Monto inválido", "Ingresá un monto mayor a 0 y hasta 10000 USD.", "warning");
      return;
    }
    const confirm = await MySwal.fire({
      title: clear ? "Quitar mensualidad especial" : "Mensualidad especial",
      html: clear
        ? `El usuario volverá a pagar la mensualidad de su plan (<b>$${renewalPrice ?? "—"}</b>).`
        : `Desde su próxima renovación, este usuario pagará <b>$${price} / mes</b>, aunque cambie de plan.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: clear ? "Quitar" : "Guardar",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;
    setSavingCustomPrice(true);
    try {
      const token = await getToken();
      const res = await setCustomMonthlyPrice({ uid: item.id, price }, token);
      setCustomPrice(res.price);
      setCustomPriceInput(res.price != null ? String(res.price) : "");
      MySwal.fire("Listo", res.price != null ? `Mensualidad especial: $${res.price} / mes` : "Mensualidad especial eliminada", "success");
      refresh();
    } catch (e) {
      MySwal.fire("Error", e.message || "No se pudo guardar la mensualidad especial.", "error");
    } finally {
      setSavingCustomPrice(false);
    }
  };

  useEffect(() => {
    if (item.birthday && item.birthday.toDate) {
      setParsedDate(dayjs(item.birthday.toDate()));
    } else if (item.birthday) {
      setParsedDate(dayjs(item.birthday));
    }
  }, [item.birthday]);

  const formik = useFormik({
    initialValues: {
      name: item.name ?? "",
      lastname: item.lastname ?? "",
      lastname2: item.lastname2 ?? "",
      phone: item.phone ?? "",
      country: item.country ?? "",
      city: item.city ?? "",
      address: item.address ?? "",
      personalId: item.personalId ?? "",
      birthday: parsedDate,
      idType: item.idType ?? "01",
      isActive: item.isActive || false,
      isGoldPlan: item.isGoldPlan || false,
      plan: item.currentPlanKey || item.plan || (item.isGoldPlan ? "elite" : "access"),
      tier:
        item.currentPlanKey === "elite" || item.isGoldPlan
          ? 3
          : item.currentPlanKey === "pro"
          ? 2
          : item.currentPlanKey === "access" || item.isActive
          ? 1
          : 0,
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      SubmitForm(values);
    },
  });

  const getStatusE = useCallback(() => {
    const unsubscribe = getExpiredStatus((statusExpired) => {
      setexpired(statusExpired);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    getStatusE();
  }, [getStatusE]);

  function handleIbToggle(checked) {
    setIbSection((prev) => ({
      ...prev,
      isIBPartner: checked,
      ibCode:
        checked && !prev.ibCode
          ? generateIbCode(item.name, item.lastname)
          : prev.ibCode,
    }));
  }

  function setIb(key, val) {
    setIbSection((prev) => ({ ...prev, [key]: val }));
  }

  const SubmitForm = async (values) => {
    const timestampBirthday = fsTimestampCreador.fromDate(
      formik.values.birthday.toDate()
    );
    const selectedTier = values.tier;
    let active = false;
    let goldPlan = false;
    let tags = Array.isArray(item.tags) ? [...item.tags] : [];
    let plan = "access";
    let currentPlanKey = null;

    if (expired) {
      MySwal.fire({ icon: "error", title: "Hubo un error", text: "Contacte a Soporte" });
      return;
    }

    if (selectedTier === 0) {
      active = false; goldPlan = false; plan = "access"; currentPlanKey = null;
    } else if (selectedTier === 1) {
      active = true; goldPlan = false; plan = "access"; currentPlanKey = "access";
    } else if (selectedTier === 2) {
      active = true; goldPlan = false; plan = "pro"; currentPlanKey = "pro";
    } else if (selectedTier === 3) {
      active = true; goldPlan = true; plan = "elite"; currentPlanKey = "elite";
    }

    MySwal.fire({
      title: "¿Confirm?",
      text: "¿Está seguro que quiere editar este usuario?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DD6B55",
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        MySwal.fire({
          icon: "warning",
          title: "Guardando...",
          allowOutsideClick: false,
          didOpen: () => { Swal.showLoading(); },
          showConfirmButton: false,
        });
        try {
          const ibPayload = ibSection.isIBPartner
            ? {
                isIBPartner: true,
                ibActive: ibSection.ibActive,
                ibCode: (
                  ibSection.ibCode.trim() ||
                  generateIbCode(item.name, item.lastname)
                ).toUpperCase(),
                commissionRate: Number(ibSection.commissionRate) / 100,
                walletAddress: ibSection.walletAddress.trim() || null,
                walletCurrency: ibSection.walletCurrency,
                ...(!item.ibCreatedAt && {
                  pendingBalance: 0,
                  totalEarned: 0,
                  referredCount: 0,
                  activeCount: 0,
                  ibCreatedAt: new Date(),
                }),
              }
            : {
                isIBPartner: false,
                ...(item.isIBPartner && { ibActive: false }),
              };

          // Billing cycle: an active plan establishes/keeps the subscription
          // window so the expiry and reminder schedulers pick the user up.
          const cyclePayload = active
            ? {
                subscriptionStatus: "active",
                planExpiresAt: fsTimestampCreador.fromDate(renewalDate.toDate()),
                ...(renewalPrice != null ? { renewalPrice } : {}),
              }
            : { subscriptionStatus: "inactive" };

          await editUser({
            id: item.id,
            name: values.name || "",
            lastname: values.lastname || "",
            lastname2: values.lastname2 ?? "",
            phone: values.phone || "",
            country: values.country || "",
            city: values.city || "",
            address: values.address || "",
            birthday: timestampBirthday,
            idType: values.idType || "",
            personalId: values.personalId || "",
            isActive: active,
            isGoldPlan: goldPlan,
            plan,
            currentPlanKey,
            tags,
            ...cyclePayload,
            ...ibPayload,
          });

          // Sync IB status to ibs_partners collection (what the landing reads)
          const ibsRef = db.collection("ibs_partners").doc(item.id);
          if (ibSection.isIBPartner) {
            const resolvedCode = (ibSection.ibCode.trim() || generateIbCode(item.name, item.lastname)).toUpperCase();
            await ibsRef.set({
              ibCode: resolvedCode,
              ibActive: ibSection.ibActive,
              commissionRate: Number(ibSection.commissionRate) / 100,
              walletAddress: ibSection.walletAddress.trim() || null,
              walletCurrency: ibSection.walletCurrency,
              ...(!item.ibCreatedAt && {
                pendingBalance: 0,
                totalEarned: 0,
                referredCount: 0,
                activeCount: 0,
                ibCreatedAt: new Date(),
              }),
            }, { merge: true });
          } else if (item.isIBPartner) {
            await ibsRef.set({ ibActive: false }, { merge: true });
          }

          MySwal.fire("Cambios Guardados!", "", "success");
          refresh();
          onClose();
        } catch (error) {
          console.error("Error saving changes:", error);
          MySwal.fire("Hubo un error al guardar los cambios", "", "error");
          onClose();
        }
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          backgroundColor: theme.palette.secondary.secondary,
          marginTop: 100,
          backgroundImage: "none",
          overflowX: "hidden",
        },
      }}
      sx={{ zIndex: 1251, width: "100%" }}
      fullWidth={true}
    >
      <DialogTitle sx={{ color: theme.palette.primary.main }}>
        Editar Usuario
        <Box sx={[styles.closeButton]} onClick={onClose}>
          <Close />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container alignItems="center" justifyContent="center">
          <Grid item md={12} sm={12}>
            <Box sx={styles.cardStyle} className="add-media-card mb-4">
              <form onSubmit={formik.handleSubmit} className="m-auto w-100">
                <Box sx={styles.innerCardStyle}>

                  <TextField
                    margin="dense" variant="standard" fullWidth
                    id="name" name="name" type="text" label="Nombre"
                    value={formik.values.name} onChange={formik.handleChange}
                  />
                  <TextField
                    margin="dense" variant="standard" fullWidth
                    id="lastname" name="lastname" type="text" label="Apellido"
                    value={formik.values.lastname} onChange={formik.handleChange}
                  />
                  <TextField
                    margin="dense" variant="standard" fullWidth
                    id="lastname2" name="lastname2" type="text" label="Segundo Apellido"
                    value={formik.values.lastname2} onChange={formik.handleChange}
                  />

                  <Grid item md={12} container justifyContent="space-between" mt={2} columnGap={2}>
                    <Grid item md={5.5}>
                      <TextField
                        margin="dense" variant="standard" fullWidth
                        id="phone" name="phone" type="phone" label="Teléfono"
                        value={formik.values.phone} onChange={formik.handleChange}
                      />
                    </Grid>
                    <Grid item md={5.5}>
                      <TextField
                        margin="dense" variant="standard" fullWidth
                        id="country" name="country" label="País"
                        value={formik.values.country} onChange={formik.handleChange}
                      />
                    </Grid>
                  </Grid>

                  <Grid item md={12} container justifyContent="space-between" mt={2} columnGap={0.5}>
                    <Grid item md={5.5}>
                      <TextField
                        margin="dense" variant="standard" fullWidth
                        id="city" name="city" label="Ciudad"
                        value={formik.values.city} onChange={formik.handleChange}
                      />
                    </Grid>
                    <Grid item md={5.5}>
                      <TextField
                        margin="dense" variant="standard" fullWidth
                        id="personalId" name="personalId" label="Número Identificación"
                        value={formik.values.personalId} onChange={formik.handleChange}
                      />
                    </Grid>
                  </Grid>

                  <Grid item md={12} container justifyContent="space-between" mt={2} columnGap={2}>
                    <Grid item md={5.5}>
                      <TextField
                        margin="dense" variant="standard" fullWidth
                        id="address" name="address" label="Dirección"
                        value={formik.values.address} onChange={formik.handleChange}
                      />
                    </Grid>
                    <Grid item md={5.5}>
                      <DatePicker
                        label="Fecha de Nacimiento"
                        value={formik.values.birthday}
                        sx={{
                          width: "100%",
                          "& .MuiIconButton-root": { color: `${Colors.greyLight} !important` },
                          "& .MuiInputBase-input": { color: `${Colors.greyLight} !important` },
                          "& .MuiFormLabel-root": { color: `${Colors.greyLight} !important` },
                        }}
                        onChange={(v) => formik.setFieldValue("birthday", v)}
                        renderInput={(params) => <TextField {...params} variant="outlined" />}
                      />
                    </Grid>
                  </Grid>

                  <Grid item md={12} mt={2}>
                    <TextField
                      fullWidth label="Membresía" id="tier" name="tier"
                      select value={formik.values.tier} onChange={formik.handleChange}
                      SelectProps={{ MenuProps: { style: { maxWidth: 0, maxHeight: 300 }, disableScrollLock: true } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start"><MdNotificationImportant /></InputAdornment>
                        ),
                      }}
                      variant="standard"
                    >
                      {memberOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* ── Subscription / billing cycle ── */}
                  <Box sx={{ width: "100%", mt: 3 }}>
                    <Divider sx={{ borderColor: Colors.borderSecondary, mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: Colors.textMuted, letterSpacing: 1.5 }}>
                        SUSCRIPCIÓN
                      </Typography>
                    </Divider>

                    <Stack spacing={2} sx={{ mt: 1 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: Colors.textSecondary }}>
                          Cuota de renovación
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                          {monthlyPrice != null ? `$${monthlyPrice} / mes` : "—"}
                          {customPrice != null ? " (especial)" : ""}
                        </Typography>
                      </Stack>

                      {isAlmighty ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            label="Mensualidad especial (USD)"
                            type="number"
                            size="small"
                            value={customPriceInput}
                            onChange={(e) => setCustomPriceInput(e.target.value)}
                            inputProps={{ min: 0, step: "0.01" }}
                            sx={{ flex: 1 }}
                          />
                          <Button
                            variant="contained" color="primary"
                            disabled={savingCustomPrice || customPriceInput === ""}
                            onClick={() => handleSaveCustomPrice(false)}
                          >
                            Guardar
                          </Button>
                          <Button
                            variant="outlined" color="primary"
                            disabled={savingCustomPrice || customPrice == null}
                            onClick={() => handleSaveCustomPrice(true)}
                          >
                            Quitar
                          </Button>
                        </Stack>
                      ) : customPrice != null ? (
                        <Typography variant="caption" sx={{ color: Colors.textMuted }}>
                          Mensualidad especial asignada por el owner. Solo el owner puede modificarla.
                        </Typography>
                      ) : null}

                      <DatePicker
                        label="Vence / Próximo pago"
                        value={renewalDate}
                        sx={{
                          width: "100%",
                          "& .MuiIconButton-root": { color: `${Colors.greyLight} !important` },
                          "& .MuiInputBase-input": { color: `${Colors.greyLight} !important` },
                          "& .MuiFormLabel-root": { color: `${Colors.greyLight} !important` },
                        }}
                        onChange={(v) => v && setRenewalDate(v)}
                        renderInput={(params) => <TextField {...params} variant="outlined" />}
                      />
                      <Typography variant="caption" sx={{ color: Colors.textMuted }}>
                        Al asignar un plan activo se inicia el ciclo de facturación desde esta fecha.
                        Usá “Registrar pago” cada vez que el cliente pague fuera de NowPayments.
                      </Typography>

                      <Button
                        variant="outlined" color="primary" fullWidth
                        disabled={savingPayment || formik.values.tier === 0}
                        onClick={handleRegisterPayment}
                      >
                        {savingPayment ? "Registrando..." : "Registrar pago (renovación +30 días)"}
                      </Button>
                    </Stack>
                  </Box>

                  {/* ── IB Partner ── */}
                  <Box sx={{ width: "100%", mt: 3 }}>
                    <Divider sx={{ borderColor: Colors.borderSecondary, mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: Colors.textMuted, letterSpacing: 1.5 }}>
                        SOCIO IB
                      </Typography>
                    </Divider>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={ibSection.isIBPartner}
                          onChange={(e) => handleIbToggle(e.target.checked)}
                          color="primary" size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: Colors.textSecondary }}>
                          Es socio IB
                        </Typography>
                      }
                    />

                    <Collapse in={ibSection.isIBPartner}>
                      <Stack spacing={2} sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-end">
                          <TextField
                            label="Código IB"
                            value={ibSection.ibCode}
                            onChange={(e) => setIb("ibCode", e.target.value)}
                            variant="standard" fullWidth
                            inputProps={{ style: { textTransform: "uppercase" } }}
                          />
                          <IconButton
                            size="small" title="Regenerar código"
                            onClick={() => setIb("ibCode", generateIbCode(item.name, item.lastname))}
                            sx={{ color: Colors.textSecondary, mb: 0.5 }}
                          >
                            <MdRefresh size={18} />
                          </IconButton>
                          <TextField
                            label="Comisión %"
                            type="number"
                            value={ibSection.commissionRate}
                            onChange={(e) => setIb("commissionRate", e.target.value)}
                            variant="standard"
                            sx={{ width: 110, flexShrink: 0 }}
                            inputProps={{ min: 0, max: 100 }}
                          />
                        </Stack>

                        <TextField
                          label="Dirección de billetera"
                          value={ibSection.walletAddress}
                          onChange={(e) => setIb("walletAddress", e.target.value)}
                          variant="standard" fullWidth
                          placeholder="Dejar vacío si no tiene"
                        />

                        <TextField
                          select label="Moneda de pago"
                          value={ibSection.walletCurrency}
                          onChange={(e) => setIb("walletCurrency", e.target.value)}
                          variant="standard" fullWidth
                          SelectProps={{ native: true }}
                        >
                          <option value="usdttrc20">USDT TRC-20</option>
                          <option value="usdterc20">USDT ERC-20</option>
                          <option value="btc">BTC</option>
                          <option value="eth">ETH</option>
                        </TextField>

                        <FormControlLabel
                          control={
                            <Switch
                              checked={ibSection.ibActive}
                              onChange={(e) => setIb("ibActive", e.target.checked)}
                              size="small" color="primary"
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: Colors.textSecondary }}>
                              Socio activo
                            </Typography>
                          }
                        />
                      </Stack>
                    </Collapse>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: "5px" }}>
                    <Button
                      className="w-100" color="primary" variant="contained"
                      fullWidth type="submit" disabled={!formik.isValid}
                    >
                      Guardar Cambios
                    </Button>
                  </Box>

                </Box>
              </form>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default UsersEditModal;
